// components/PurchaseOrderPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
} from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: 65,
    lineHeight: 1.4,
    color: "#000",
  },
  columnBox: {
    width: "33.33%", // 3 in a row
    padding: 4,
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    color: "#0b57d0",
    fontWeight: "bold",
    marginBottom: 10,
  },
  section: {
    marginBottom: 10,
  },
  bold: {
    fontWeight: "bold",
  },
  headerLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    marginBottom: 6,
    paddingBottom: 4,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
  },
  column: {
    flex: 1,
    paddingRight: 5,
  },
  smallText: {
    fontSize: 9,
  },
  link: {
    color: "#0b57d0",
    textDecoration: "none",
  },
});

const twoDecimal = (x) => {
  // Convert to a float and format to two decimal places
  const num = Number.parseFloat(x).toFixed(2);

  // Split the integer and decimal parts
  const [integerPart, decimalPart] = num.split(".");

  // Format the integer part with a proper regex for Indian numbering system
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");

  // Return the formatted value with the decimal part
  return `${formattedInteger}.${decimalPart}`;
};

function roundUpToInteger(value) {
  const roundedValue = Math.ceil(value);
  const num = roundedValue.toFixed(2);
  // Split the integer and decimal parts
  const [integerPart, decimalPart] = num.split(".");

  // Format the integer part with a proper regex for Indian numbering system
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");

  // Return the formatted value with the decimal part
  return `${formattedInteger}.${decimalPart}`;
}

function calculateRoundingDifference(value) {
  const roundedValue = Math.ceil(value);
  const difference = roundedValue - value;

  return difference.toFixed(2);
}

const PDFDocument = ({
  poDetails,
  paymentList,
  poid,
  projectDetails,
  ClassificationData,
  companyLogo,
}) => {
  function stripHtml(html) {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }
  const poNo = poDetails?.po_no || ""; // fallback to empty string if undefined
  const match = poNo.match(/T(\d+)\//);
  const extractedNumber = match ? match[1] : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <View
          style={{
            backgroundColor: "#0056b3", // Blue background
            paddingVertical: 8,
            paddingHorizontal: 16,
            width: "100%", // Full width
            marginBottom: 16,
          }}
        >
          <Text
            style={{
              color: "white",
              fontSize: 16,
              fontWeight: "bold",
              textAlign: "center", // Center text within full-width box
            }}
          >
            {poDetails.order_type == "1" ? "Purchase Order" : "Work Order"}
          </Text>
        </View>

        {/* PO Number */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end", // Align content to the right
            borderBottomWidth: 1,
            borderBottomColor: "#000",
            marginTop: 8,
            marginBottom: 10,
          }}
        >
          <Text style={styles.bold}>
            {extractedNumber} {poDetails.vendor_name}{" "}
            {moment(poDetails.po_date).format("DD-MM-YYYY")} @
            {poDetails.project_name}
          </Text>
        </View>
        {/* Company Info and Logo Row */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottomWidth: 1,
            borderBottomColor: "#000",
            marginTop: 8,
            marginBottom: 10,
          }}
        >
          {/* Left: Logo */}
          <View
            style={{
              width: "30%",
              alignItems: "center", // Centers the image horizontally
              justifyContent: "center", // (Optional) if you want vertical
              marginTop: 20,
            }}
          >
            <Image
              src={companyLogo}
              style={{
                width: 140, // Increase width
                height: 60, // Increase height proportionally
                objectFit: "contain", // Ensures it scales without distortion
              }}
            />
          </View>

          {/* Right: Company Info */}
          <View style={{ width: "70%", alignItems: "flex-end" }}>
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 10,
                marginTop: 4,
                color: "#0056b3",
              }}
            >
              {poDetails.company_Name}
            </Text>

            <Text style={{ fontSize: 10, marginTop: 4 }}>
              {poDetails
                ? poDetails.company_address?.trim().replace(/\s+/g, " ")
                : `Plot No 108, Perumal Koil Street, Alapakkam Porur`}
            </Text>

            <Text style={{ fontSize: 10, marginTop: 4 }}>
              {poDetails
                ? `${poDetails.company_city} - ${poDetails.company_pincode}`
                : `Chennai - 600116`}
            </Text>

            <Text style={{ fontSize: 10, marginTop: 4 }}>
              {poDetails
                ? poDetails.company_website
                : `https://kgbuildtech.com`}
            </Text>

            <Text style={{ fontSize: 10, marginTop: 4 }}>
              {poDetails ? `GST NO: ${poDetails.gst_no}` : ``}
            </Text>
          </View>
        </View>
        <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 12 }}>
          {/* First Row (3 items) */}
          <View style={styles.columnBox}>
            <Text>
              <Text>
                {poDetails.order_type == "1" ? "PO Order No:" : "WO Order No:"}
              </Text>{" "}
              {poDetails.po_no}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              <Text>
                {poDetails.order_type == "1" ? "PO Date:" : "WO Date:"}
              </Text>{" "}
              {moment(poDetails.po_date).format("DD-MM-YYYY")}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              <Text>Supplier Ref:</Text> {poDetails.quotation_no}
            </Text>
          </View>

          {/* Second Row (3 items) */}
          <View style={styles.columnBox}>
            <Text>
              <Text>
                {poDetails.order_type == "1"
                  ? "PO Classification:"
                  : "WO Classification:"}
              </Text>{" "}
              {poDetails.clasification_name}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              Site Engineer:{" "}
              <Text style={{ color: "#0056b3" }}>
                {poDetails.site_eng_name}
              </Text>
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              Contact:{" "}
              <Text style={{ color: "#0056b3" }}>
                {poDetails.site_eng_contact}
              </Text>
            </Text>
          </View>
        </View>

        {/* Details Section: 3 Columns */}
        <View style={[styles.row, { marginTop: 12 }]}>
          {/* Column 1: Bill To */}
          <View style={styles.column}>
            <Text style={styles.bold}>Bill To :</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#0056b3",
                fontWeight: "bold",
                marginTop: 4,
              }}
            >
              {poDetails.vendor_name}
            </Text>
            <Text style={{ marginTop: 4 }}>{poDetails.vendor_mobile}</Text>

            <Text style={{ marginTop: 4 }}>{poDetails.vendor_street}</Text>
            <Text style={{ marginTop: 4 }}>{poDetails.vendor_city}</Text>
            {poDetails.vendor_state?.toLowerCase() !== "tamilnadu" && (
              <>
                <Text style={{ marginTop: 4 }}>{poDetails.vendor_state}</Text>
                <Text style={{ marginTop: 4 }}>India,</Text>
              </>
            )}

            <Text style={{ marginTop: 4 }}>{poDetails.vendor_pincode}</Text>
            <Text style={{ marginTop: 4 }}>GSTIN : {poDetails.vendor_gst}</Text>
          </View>

          {/* Column 2: Supply To */}
          <View style={styles.column}>
            <Text style={{ marginTop: 4 }}>Place of Supply :</Text>

            <Text
              style={{
                fontSize: 12,
                color: "#0056b3",
                fontWeight: "bold",
                marginTop: 4,
              }}
            >
              {projectDetails.name}
            </Text>
            <Text style={{ marginTop: 4 }}>
              {projectDetails.project_address}
            </Text>
            <Text style={{ marginTop: 4 }}>{projectDetails.city}</Text>

            {projectDetails.state?.toLowerCase() !== "tamilnadu" && (
              <>
                <Text style={{ marginTop: 4 }}>{projectDetails.state}</Text>
                <Text style={{ marginTop: 4 }}>India,</Text>
              </>
            )}

            <Text style={{ marginTop: 4 }}>{projectDetails.pincode}</Text>
          </View>
        </View>
        <View>
          <View
            style={{
              marginTop: 5,
              flexDirection: "row",
              borderTop: "1 solid black",
              backgroundColor: "#f0f0f0",
              fontWeight: "bold",
            }}
          >
            <Text
              style={{
                width: "7%",
                padding: 5,
                fontSize: 10,
                textAlign: "center",
                borderRight: "1 solid black",
                borderLeft: "1 solid black",
              }}
            >
              S.No
            </Text>
            <Text
              style={{
                width: "50%",
                padding: 5,
                fontSize: 10,
                textAlign: "center",
                borderRight: "1 solid black",
              }}
            >
              Description of Goods
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 5,
                fontSize: 10,
                textAlign: "center",
                borderRight: "1 solid black",
              }}
            >
              UNO
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 5,
                fontSize: 10,
                textAlign: "center",
                borderRight: "1 solid black",
              }}
            >
              Amount (Rs)
            </Text>
            <Text
              style={{
                width: "10%",
                padding: 5,
                fontSize: 10,
                textAlign: "center",
                borderRight: "1 solid black",
              }}
            >
              GST
            </Text>
            <Text
              style={{
                width: "15%",
                padding: 5,
                fontSize: 10,

                textAlign: "center",
                borderRight: "1 solid black",
              }}
            >
              Total Amount
            </Text>
          </View>

          {paymentList &&
            paymentList.map((item, index) => {
              return (
                <View
                  style={{
                    flexDirection: "row",
                    borderBottom: "1 solid black",
                    borderTop: index === 0 ? "1 solid black" : "none",
                    fontWeight: "normal",
                  }}
                >
                  <Text
                    style={{
                      width: "7%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "center",
                      borderRight: "1 solid black",
                      borderLeft: "1 solid black",
                      // color: "#003366",
                    }}
                  >
                    {index + 1}
                  </Text>
                  <Text
                    style={{
                      width: "50%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "left",
                      borderRight: "1 solid black",
                      // color: "#003366",
                    }}
                  >
                    {item.item_name}
                  </Text>
                  <Text
                    style={{
                      width: "10%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "center",
                      borderRight: "1 solid black",
                    }}
                  >
                    {twoDecimal(item.quantity)} {"\n"}({item.unit_name})
                  </Text>
                  <Text
                    style={{
                      width: "10%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "center",
                      borderRight: "1 solid black",
                    }}
                  >
                    {twoDecimal(item.amount)}
                  </Text>
                  <Text
                    style={{
                      width: "10%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "center",
                      borderRight: "1 solid black",
                    }}
                  >
                    {item.gst}%
                  </Text>
                  <Text
                    style={{
                      width: "15%",
                      padding: 5,
                      fontSize: 10,
                      textAlign: "center",
                      borderRight: "1 solid black",
                    }}
                  >
                    {twoDecimal(item.total_amount_with_gst)}
                  </Text>
                </View>
              );
            })}
        </View>
        <View
          style={{
            flexDirection: "row",

            // borderBottomWidth: 1,
            borderColor: "black",
            borderLeftWidth: 1,
          }}
        >
          <Text
            style={{
              width: "55%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderColor: "black",
            }}
          ></Text>
          <Text
            style={{
              width: "20%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderLeftWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            Total
          </Text>

          <Text
            style={{
              width: "25%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            {twoDecimal(poDetails.total_without_gst)}
          </Text>
        </View>

        {poDetails.total_igst > 0 ? (
          <View
            style={{
              flexDirection: "row",

              // borderBottomWidth: 1,
              borderColor: "black",
              borderLeftWidth: 1,
            }}
          >
            <Text
              style={{
                width: "55%",
                padding: 5,
                fontSize: 10,
                textAlign: "right",
                borderColor: "black",
              }}
            ></Text>
            <Text
              style={{
                width: "20%",
                padding: 5,
                fontSize: 10,
                textAlign: "right",
                borderRightWidth: 1,
                borderLeftWidth: 1,
                borderBottomWidth: 1,
                borderColor: "black",
              }}
            >
              IGST
            </Text>

            <Text
              style={{
                width: "25%",
                padding: 5,
                fontSize: 10,
                textAlign: "right",
                borderRightWidth: 1,
                borderBottomWidth: 1,
                borderColor: "black",
              }}
            >
              {twoDecimal(poDetails.total_igst)}
            </Text>
          </View>
        ) : (
          <>
            <View
              style={{
                flexDirection: "row",
                // borderBottomWidth: 1,
                borderColor: "black",
                borderLeftWidth: 1,
              }}
            >
              <Text
                style={{
                  width: "55%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderColor: "black",
                }}
              ></Text>
              <Text
                style={{
                  width: "20%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderRightWidth: 1,
                  borderLeftWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: "black",
                }}
              >
                CGST
              </Text>

              <Text
                style={{
                  width: "25%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderRightWidth: 1,
                  borderBottomWidth: 1,

                  borderColor: "black",
                }}
              >
                {twoDecimal(poDetails.total_cgst)}
              </Text>
            </View>{" "}
            <View
              style={{
                flexDirection: "row",

                // borderBottomWidth: 1,
                borderColor: "black",
                borderLeftWidth: 1,
              }}
            >
              <Text
                style={{
                  width: "55%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderColor: "black",
                }}
              ></Text>
              <Text
                style={{
                  width: "20%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderRightWidth: 1,
                  borderLeftWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: "black",
                }}
              >
                SGST
              </Text>

              <Text
                style={{
                  width: "25%",
                  padding: 5,
                  fontSize: 10,
                  textAlign: "right",
                  borderRightWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: "black",
                }}
              >
                {twoDecimal(poDetails.total_sgst)}
              </Text>
            </View>
          </>
        )}

        <View
          style={{
            flexDirection: "row",

            // borderBottomWidth: 1,
            borderColor: "black",
            borderLeftWidth: 1,
          }}
        >
          <Text
            style={{
              width: "55%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderColor: "black",
            }}
          ></Text>
          <Text
            style={{
              width: "20%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderLeftWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            TRS GST
          </Text>

          <Text
            style={{
              width: "25%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            {poDetails.transport_gst}%
          </Text>
        </View>
        <View
          style={{
            flexDirection: "row",
            // borderBottomWidth: 1,
            borderColor: "black",
            borderLeftWidth: 1,
          }}
        >
          <Text
            style={{
              width: "55%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderColor: "black",
            }}
          ></Text>
          <Text
            style={{
              width: "20%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderLeftWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            TRS Charge
          </Text>
          <Text
            style={{
              width: "25%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderBottomWidth: 1,
              borderColor: "black",
            }}
          >
            {twoDecimal(poDetails.transport_charge)}
          </Text>
        </View>

        {/* row 7 */}
        <View
          style={{
            flexDirection: "row",
            borderBottomWidth: 1,
            borderColor: "black",
            borderLeftWidth: 1,
          }}
        >
          <Text
            style={{
              width: "55%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderColor: "black",
            }}
          ></Text>

          <Text
            style={{
              width: "20%",
              padding: 5,
              fontSize: 12,
              textAlign: "right",
              borderRightWidth: 1,
              borderLeftWidth: 1,
              borderColor: "black",
              color: "#0056b3",
            }}
          >
            Payable Amount
          </Text>

          {/* Amount Value */}
          <Text
            style={{
              width: "25%",
              padding: 5,
              fontSize: 10,
              textAlign: "right",
              borderRightWidth: 1,
              borderColor: "black",
            }}
          >
            {twoDecimal(poDetails.total_with_gst)}
          </Text>
        </View>

        {/* Terms & Conditions */}
        <View style={{ marginTop: 10, fontSize: 10, fontFamily: "Helvetica" }}>
          <View
            wrap={false} // 🔒 Prevents breaking across pages
            style={{ border: "1 solid black" }}
          >
            <Text
              style={{
                backgroundColor: "#eceff1",
                textAlign: "center",
                fontWeight: "bold",
                padding: 6,
                fontSize: 11,
              }}
            >
              {poDetails.order_type == "1"
                ? "PO Terms & Condition(s):"
                : "WO Terms & Condition(s):"}
            </Text>
            <Text
              style={{
                textAlign: "center",
                padding: 6,
                fontSize: 11,
                color: "#2f3e55",
                borderTop: "1 solid black",
              }}
            >
              {stripHtml(poDetails?.terms)}
            </Text>
          </View>

          {/* Bank Details */}
          <View
            wrap={false}
            style={{
              marginTop: 20,
              padding: 10,
              border: "1 solid #2f3e55",
              borderRadius: 6,
              backgroundColor: "#f8f9fa",
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 12,
                marginBottom: 8,
                color: "#2f3e55",
              }}
            >
              Bank Details
            </Text>

            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              {/* Left Column */}
              <View style={{ flex: 1, marginRight: 10 }}>
                <Text style={{ marginBottom: 4 }}>
                  <Text style={{ fontWeight: "bold" }}>Bank Name: </Text>
                  {poDetails.bank_name}
                </Text>
                <Text style={{ marginBottom: 4 }}>
                  <Text style={{ fontWeight: "bold" }}>Account Name: </Text>
                  {poDetails.account_name}
                </Text>
                <Text style={{ marginBottom: 4 }}>
                  <Text style={{ fontWeight: "bold" }}>Account Number: </Text>
                  {poDetails.account_no}
                </Text>
              </View>

              {/* Right Column */}
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={{ marginBottom: 4 }}>
                  <Text style={{ fontWeight: "bold" }}>Branch: </Text>
                  {poDetails.branch}
                </Text>
                <Text style={{ marginBottom: 4 }}>
                  <Text style={{ fontWeight: "bold" }}>IFSC Code: </Text>
                  {poDetails.ifsc_code}
                </Text>
              </View>
            </View>
          </View>
        </View>
        {/* Signatures */}
        <View
          style={{
            position: "absolute", // Stick to bottom
            bottom: 40, // Distance from bottom edge
            left: 40, // Optional padding from left
            right: 40, // Optional padding from right
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 12 }}>
            Prepared By
          </Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 12 }}>
            Verified By
          </Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 12 }}>
            Approved By
          </Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 12 }}>
            Approved By QS
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;
